import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ProjectApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    compactChannelName,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartMultiplier,
    formatChartNumber,
    ExpandablePanelTitle,
    filterRowsBySearch,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
    SortableHeader,
    TablePagination,
    TableSearch,
    useSortableRows,
} from "./ReportingLab.jsx";

const CampaignSegment = {
    AboveBreakEven: "aboveBreakEven",
    Loss: "loss",
    LowData: "lowData",
};

function getCampaignMetric(campaign, metricBasis, key) {
    const suffix = metricBasis === "completed" ? "Net" : "Gross";
    return campaign?.[`${key}${suffix}`];
}

export function buildCampaignRows(channelData, t) {
    const rows = [];

    channelData.forEach(({channel, overview}) => {
        const channelName = compactChannelName(channel);
        const campaigns = overview?.adAccounts?.flatMap((account) => account.campaigns ?? []) ?? [];

        campaigns.forEach((campaign) => {
            const spend = campaign.spendMarketing;
            const turnover = getCampaignMetric(campaign, "marketing", "turnover");
            const profit = getCampaignMetric(campaign, "marketing", "profit");
            // OpenAPI deserialization keeps omitted optional properties as keys with an
            // undefined value. The value itself is therefore the permission boundary:
            // when the backend redacts profit metrics, neither profit nor POAS may be
            // rendered or used for profitability classification.
            const hasProfit = campaign.profitGross != null;
            const hasPoas = campaign.poasBeforeMarketingGross != null;
            const orders = Number(getCampaignMetric(campaign, "marketing", "orders") ?? 0);
            const poas = campaign.poasBeforeMarketingGross ?? null;
            const roas = campaign.roasGross ?? null;
            const pno = campaign.pnoGross ?? null;
            const spendAmount = moneyAmount(spend);
            const turnoverAmount = moneyAmount(turnover);
            const profitAmount = moneyAmount(profit);
            const clicks = Number(campaign.clicks ?? 0);
            const impressions = Number(campaign.impressions ?? 0);

            let segment = CampaignSegment.LowData;

            if (!hasProfit || !hasPoas) {
                if (spendAmount > 0 && orders >= 3) {
                    segment = turnoverAmount >= spendAmount
                        ? CampaignSegment.AboveBreakEven
                        : CampaignSegment.Loss;
                }
            } else if (spendAmount > 0 && profitAmount < 0) {
                segment = CampaignSegment.Loss;
            } else if (spendAmount > 0 && orders < 3) {
                segment = CampaignSegment.LowData;
            } else if (hasProfit && hasPoas && spendAmount > 0 && poas !== null && poas >= 100) {
                segment = CampaignSegment.AboveBreakEven;
            } else if (hasProfit && hasPoas && spendAmount > 0) {
                segment = CampaignSegment.Loss;
            }
            segment = hasProfit && hasPoas ? (campaign.performanceSegmentGross ?? segment) : segment;

            rows.push({
                id: `${channel?.id ?? channelName}-${campaign.remoteId ?? campaign.name}`,
                channel,
                channelName,
                campaign,
                name: campaign.name ?? t("reporting_lab.campaigns.unknown_campaign", {defaultValue: "Neznámá kampaň"}),
                status: campaign.statusName ?? campaign.status ?? "-",
                spend,
                turnover,
                ...(hasProfit ? {profit} : {}),
                orders,
                ...(hasPoas ? {poas} : {}),
                roas,
                pno,
                cpa: campaign.cpa,
                clicks,
                impressions,
                ctr: impressions > 0 ? (clicks / impressions) * 100 : null,
                cpc: clicks > 0 ? {...spend, amount: spendAmount / clicks} : null,
                cpm: impressions > 0 ? {...spend, amount: spendAmount / impressions * 1000} : null,
                costPerOrder: orders > 0 ? {...spend, amount: spendAmount / orders} : null,
                conversionRate: clicks > 0 ? (orders / clicks) * 100 : null,
                averageOrderValue: orders > 0 ? {...turnover, amount: moneyAmount(turnover) / orders} : null,
                segment,
            });
        });
    });

    return rows
        .filter((row) => moneyAmount(row.spend) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0 || row.clicks > 0)
        .sort((a, b) => moneyAmount(b.spend) - moneyAmount(a.spend));
}

function campaignTableValue(row, key) {
    return key === "campaign" ? row.name : key === "channel" ? row.channelName : row[key];
}

function segmentMeta(t, profitClassification) {
    return {
        [CampaignSegment.AboveBreakEven]: {
            label: profitClassification
                ? t("reporting_lab.campaigns.segment.above_break_even", {defaultValue: "Ziskové"})
                : t("reporting_lab.campaigns.segment.performing", {defaultValue: "Výkonné"}),
            tone: "good",
            className: "border-emerald-200 bg-emerald-50 text-emerald-700",
        },
        [CampaignSegment.Loss]: {
            label: profitClassification
                ? t("reporting_lab.campaigns.segment.loss", {defaultValue: "Ztrátové"})
                : t("reporting_lab.campaigns.segment.underperforming", {defaultValue: "Nevýkonné"}),
            tone: "bad",
            className: "border-rose-200 bg-rose-50 text-rose-700",
        },
        [CampaignSegment.LowData]: {
            label: t("reporting_lab.campaigns.segment.low_data", {defaultValue: "Málo dat"}),
            tone: "default",
            className: "border-slate-200 bg-white text-slate-500",
        },
    };
}

function SegmentBadge({segment, meta}) {
    return <span className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${meta[segment]?.className ?? meta[CampaignSegment.LowData].className}`}>
        {meta[segment]?.label ?? segment}
    </span>;
}

export default function ReportingCampaignsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderRatioValue = (value) => formatChartMultiplier(value);
    const [channelData, setChannelData] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSegment, setSelectedSegment] = useState("all");
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const tableExpandedStorageKey = `reporting-lab:campaign-table-expanded:${project?.id ?? "unknown"}`;
    const [tableExpanded, setTableExpanded] = useState(() => typeof window !== "undefined" && window.sessionStorage.getItem(tableExpandedStorageKey) === "1");

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        window.sessionStorage.setItem(tableExpandedStorageKey, tableExpanded ? "1" : "0");
    }, [tableExpanded, tableExpandedStorageKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setChannelData([]);
        setSnapshot(null);

        const projectApi = new ProjectApi(getAuthorityConfiguration());
        projectApi.marketingChannelOverviews({projectId: project.id, erpFilter})
            .then((result) => {
                if (!active) {
                    return;
                }

                setChannelData((result.items ?? []).map((item) => ({
                    channel: {id: item.id, name: item.name},
                    overview: item.overview,
                })));
                setSnapshot(result.snapshot);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const campaigns = useMemo(() => buildCampaignRows(channelData, t), [channelData, t]);
    const hasProfit = campaigns.some((campaign) => campaign.profit != null);
    const hasPoas = campaigns.some((campaign) => campaign.poas != null);
    const usesProfitClassification = hasProfit && hasPoas;
    const meta = segmentMeta(t, usesProfitClassification);
    const segmentCampaigns = selectedSegment === "all" ? campaigns : campaigns.filter((campaign) => campaign.segment === selectedSegment);
    const filteredCampaigns = filterRowsBySearch(segmentCampaigns, tableSearch, (campaign) => [campaign.name, campaign.channelName]);
    const {sortedRows: sortedCampaigns, sort: tableSort, toggleSort} = useSortableRows(
        filteredCampaigns,
        "spend",
        campaignTableValue,
    );
    const tablePages = Math.max(1, Math.ceil(sortedCampaigns.length / tablePageSize));
    const normalizedTablePage = Math.min(tablePage, tablePages);
    const visibleCampaigns = sortedCampaigns.slice((normalizedTablePage - 1) * tablePageSize, normalizedTablePage * tablePageSize);

    useEffect(() => {
        setTablePage(1);
    }, [selectedSegment, campaigns.length, tableSearch]);
    const segmentCounts = useMemo(() => {
        return campaigns.reduce((counts, campaign) => {
            counts[campaign.segment] = (counts[campaign.segment] ?? 0) + 1;
            return counts;
        }, {});
    }, [campaigns]);
    const totals = useMemo(() => campaigns.reduce((accumulator, campaign) => {
        accumulator.spend += moneyAmount(campaign.spend);
        accumulator.profit += moneyAmount(campaign.profit);
        accumulator.turnover += moneyAmount(campaign.turnover);
        accumulator.orders += campaign.orders;
        return accumulator;
    }, {spend: 0, profit: 0, turnover: 0, orders: 0}), [campaigns]);
    const currency = campaigns.find((campaign) => campaign.spend?.currency)?.spend?.currency ?? projectCurrency(project);
    const aboveBreakEvenCampaigns = campaigns.filter((campaign) => campaign.segment === CampaignSegment.AboveBreakEven).sort((a, b) => moneyAmount(b.profit) - moneyAmount(a.profit)).slice(0, 6);
    const lossCampaigns = campaigns.filter((campaign) => campaign.segment === CampaignSegment.Loss).sort((a, b) => moneyAmount(a.profit) - moneyAmount(b.profit)).slice(0, 6);
    const topSpendCampaigns = [...campaigns].sort((a, b) => moneyAmount(b.spend) - moneyAmount(a.spend)).slice(0, 10);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const spendProfitOption = {
        color: ["#7c3aed", "#16a34a"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 28, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...topSpendCampaigns].reverse().map((campaign) => campaign.name),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.table.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: [...topSpendCampaigns].reverse().map((campaign) => moneyToMajor(campaign.spend)),
                barMaxWidth: 24,
            },
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Marže"}),
                type: "bar",
                data: [...topSpendCampaigns].reverse().map((campaign) => moneyToMajor(campaign.profit)),
                barMaxWidth: 24,
            },
        ],
    };

    const profitRankingOption = {
        color: ["#16a34a", "#ef4444"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 24, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...lossCampaigns].reverse().map((campaign) => campaign.name),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Marže"}),
                type: "bar",
                data: [...lossCampaigns].reverse().map((campaign) => moneyToMajor(campaign.profit)),
                barMaxWidth: 24,
            },
        ],
    };
    const campaignTableActions = <div className="flex flex-wrap items-center justify-end gap-2">
        <button
            type="button"
            onClick={() => setSelectedSegment("all")}
            className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold ${selectedSegment === "all" ? "border-violet-500 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {t("reporting_lab.campaigns.all", {defaultValue: "Vše"})} ({renderNumber(campaigns.length)})
        </button>
        {Object.values(CampaignSegment).map((segment) => <button
            key={segment}
            type="button"
            onClick={() => setSelectedSegment(segment)}
            className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-semibold ${selectedSegment === segment ? "border-violet-500 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {meta[segment].label} ({renderNumber(segmentCounts[segment] ?? 0)})
        </button>)}
    </div>;
    const campaignTableTitle = <ExpandablePanelTitle
        title={t("reporting_lab.campaigns.table", {defaultValue: "Přehled kampaní"})}
        expanded={tableExpanded}
        onToggle={() => setTableExpanded((expanded) => !expanded)}
        expandLabel={t("reporting_lab.table.expand", {defaultValue: "Rozšířit"})}
        collapseLabel={t("reporting_lab.table.collapse", {defaultValue: "Zúžit"})}
    />;
    return <WideSecureLayout
        title={t("reporting_lab.pages.campaigns", {defaultValue: "Kampaně"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.campaigns.title", {defaultValue: "K čemu slouží přehled kampaní"})}
                >
                    {usesProfitClassification
                        ? t("reporting_lab.descriptions.campaigns.text", {defaultValue: "Rozděluje kampaně podle matematického break-even POAS, marže a objemu dat. Doporučení ke škálování vyžaduje projektové cíle a není odvozováno z univerzálního limitu."})
                        : t("reporting_lab.descriptions.campaigns.marketing_text", {defaultValue: "Vyhodnocuje marketingový výkon kampaní podle obratu, spendu a objemu dat. Nepracuje s nákupními cenami ani neodhaluje marži nebo zisk."})}
                </ReportingPageDescription>
                <div className="col-span-12 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{t("reporting_lab.campaigns.segment_rules_title", {defaultValue: "Jak číst segmenty kampaní"})}</div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                        <span><strong>{meta[CampaignSegment.AboveBreakEven].label}</strong>: {usesProfitClassification
                            ? t("reporting_lab.campaigns.segment_rules.above_break_even", {defaultValue: "POAS alespoň 1,0x a nezáporná marže"})
                            : t("reporting_lab.campaigns.segment_rules.performing", {defaultValue: "obrat je alespoň stejně vysoký jako marketingový spend"})}</span>
                        <span><strong>{meta[CampaignSegment.Loss].label}</strong>: {usesProfitClassification
                            ? t("reporting_lab.campaigns.segment_rules.loss", {defaultValue: "POAS pod 1,0x nebo záporná marže"})
                            : t("reporting_lab.campaigns.segment_rules.underperforming", {defaultValue: "marketingový spend je vyšší než obrat"})}</span>
                        <span><strong>{meta[CampaignSegment.LowData].label}</strong>: {t("reporting_lab.campaigns.segment_rules.low_data", {defaultValue: "méně než 3 objednávky"})}</span>
                    </div>
                </div>

                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.campaigns.total_campaigns", {defaultValue: "Kampaně"})} value={renderNumber(campaigns.length)} meta={t("reporting_lab.campaigns.with_data", {defaultValue: "Se spendem, objednávkami nebo kliky"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={meta[CampaignSegment.AboveBreakEven].label} value={renderNumber(segmentCounts[CampaignSegment.AboveBreakEven] ?? 0)} meta={t("reporting_lab.campaigns.above_break_even_meta", {defaultValue: "Nejde automaticky o doporučení ke škálování"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.marketing_spend", {defaultValue: "Marketingové náklady"})} value={renderCurrencyValue({amount: totals.spend, currency}, true)} meta={`${renderNumber(totals.orders)} ${t("reporting_lab.table.orders_short", {defaultValue: "Obj."})}`}/>
                </div>
                {hasProfit && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.profit", {defaultValue: "Marže"})} value={renderCurrencyValue({amount: totals.profit, currency}, true)} meta={`${meta[CampaignSegment.Loss].label}: ${renderNumber(segmentCounts[CampaignSegment.Loss] ?? 0)}`} tone={totals.profit < 0 ? "bad" : "default"}/>
                </div>}

                {hasProfit && <Panel loading={loading} title={t("reporting_lab.campaigns.spend_profit", {defaultValue: "Marketing vs. marže top kampaní"})} className="col-span-12 xl:col-span-7">
                    <EChart option={spendProfitOption} className="h-96"/>
                </Panel>}
                {hasProfit && <Panel loading={loading} title={t("reporting_lab.campaigns.loss_ranking", {defaultValue: "Kampaně k omezení"})} className="col-span-12 xl:col-span-5">
                    <EChart option={profitRankingOption} className="h-96"/>
                </Panel>}

                <div className="col-span-12 grid grid-cols-12 gap-4">
                {hasProfit && hasPoas && <Panel loading={loading} title={t("reporting_lab.campaigns.above_break_even", {defaultValue: "Nejziskovější kampaně"})} className={`col-span-12 ${tableExpanded ? "order-2 xl:col-span-12" : "order-1 xl:col-span-4"}`}>
                    <div className="space-y-3">
                        {aboveBreakEvenCampaigns.map((campaign) => <div key={campaign.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                            <div className="font-semibold text-slate-900 dark:text-slate-100">{campaign.name}</div>
                            <div className="mt-1 text-xs text-slate-500">{campaign.channelName}</div>
                            <div className="mt-2 flex items-center justify-between text-sm">
                                <span className={`whitespace-nowrap font-semibold ${moneyAmount(campaign.profit) < 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-700 dark:text-slate-200"}`}>{renderCurrencyValue(campaign.profit, true)}</span>
                                <span className="font-semibold text-emerald-600">POAS {renderRatioValue(campaign.poas)}</span>
                            </div>
                        </div>)}
                        {aboveBreakEvenCampaigns.length === 0 && <div className="text-sm text-slate-500">{t("reporting_lab.campaigns.no_above_break_even", {defaultValue: "Pro vybrané období nejsou ziskové kampaně s dostatečným objemem dat."})}</div>}
                    </div>
                </Panel>}

                <Panel loading={loading} title={campaignTableTitle} actions={campaignTableActions} className={`col-span-12 ${tableExpanded || !hasProfit || !hasPoas ? "order-1 xl:col-span-12" : "order-2 xl:col-span-8"}`}>
                    <TableSearch value={tableSearch} onChange={setTableSearch} visible={segmentCampaigns.length > 15} t={t}/>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <SortableHeader label={t("reporting_lab.campaigns.campaign", {defaultValue: "Kampaň"})} sortKey="campaign" sort={tableSort} onSort={toggleSort}/>
                                <SortableHeader label={t("reporting_lab.table.channel", {defaultValue: "Kanál"})} sortKey="channel" sort={tableSort} onSort={toggleSort}/>
                                <SortableHeader label={t("reporting_lab.campaigns.segment_title", {defaultValue: "Segment"})} sortKey="segment" sort={tableSort} onSort={toggleSort}/>
                                <SortableHeader label={t("reporting_lab.table.marketing", {defaultValue: "Marketing"})} sortKey="spend" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="turnover" sort={tableSort} onSort={toggleSort} align="right"/>
                                {hasProfit && <SortableHeader
                                    label={t("reporting_lab.table.contribution", {defaultValue: "Marže"})}
                                    tooltip={t("reporting_lab.table.contribution_help", {defaultValue: "Marže = obrat přiřazených objednávek minus nákupní cena zboží a marketingové náklady. Nejde o čistý účetní zisk; nezahrnuje interní náklady dopravy, plateb ani režii."})}
                                    sortKey="profit"
                                    sort={tableSort}
                                    onSort={toggleSort}
                                    align="right"
                                />}
                                {[["ROAS", "roas"], ["PNO", "pno"]].map(([label, key]) => <SortableHeader key={key} label={label} sortKey={key} sort={tableSort} onSort={toggleSort} align="right"/>)}
                                {hasPoas && <SortableHeader label="POAS" sortKey="poas" sort={tableSort} onSort={toggleSort} align="right"/>}
                                <SortableHeader label={t("reporting_lab.table.clicks", {defaultValue: "Kliky"})} sortKey="clicks" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.impressions", {defaultValue: "Imprese"})} sortKey="impressions" sort={tableSort} onSort={toggleSort} align="right"/>
                                {[["CTR", "ctr"], ["CPC", "cpc"], ["CPM", "cpm"]].map(([label, key]) => <SortableHeader key={key} label={label} sortKey={key} sort={tableSort} onSort={toggleSort} align="right"/>)}
                                <SortableHeader label={t("reporting_lab.table.cost_per_order", {defaultValue: "Cena / obj."})} sortKey="costPerOrder" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.conversion_rate", {defaultValue: "Konverze"})} sortKey="conversionRate" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.aov", {defaultValue: "AOV"})} sortKey="averageOrderValue" sort={tableSort} onSort={toggleSort} align="right"/>
                                <SortableHeader label={t("reporting_lab.table.orders_short", {defaultValue: "Obj."})} sortKey="orders" sort={tableSort} onSort={toggleSort} align="right"/>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {visibleCampaigns.map((campaign) => <tr key={campaign.id}>
                                <td className="max-w-[340px] truncate py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{campaign.name}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{campaign.channelName}</td>
                                <td className="py-3 pr-4"><SegmentBadge segment={campaign.segment} meta={meta}/></td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.spend, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.turnover, true)}</td>
                                {hasProfit && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.profit, true)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderRatioValue(campaign.roas)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{campaign.pno === null ? "-" : `${formatChartNumber(campaign.pno)} %`}</td>
                                {hasPoas && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderRatioValue(campaign.poas)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(campaign.clicks)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(campaign.impressions)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{campaign.ctr === null ? "-" : `${formatChartNumber(campaign.ctr)} %`}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.cpc, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.cpm, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.costPerOrder, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{campaign.conversionRate === null ? "-" : `${formatChartNumber(campaign.conversionRate)} %`}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(campaign.averageOrderValue, true)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(campaign.orders)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination page={normalizedTablePage} pageSize={tablePageSize} totalItems={filteredCampaigns.length} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>
                </Panel>
                </div>
            </div>
    </WideSecureLayout>;
}
